import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  Heart,
  Tag,
  Users,
  Lock,
  Unlock,
  ChevronDown,
  Pencil,
  Trash2,
  MoreHorizontal,
  X,
  Loader2,
} from 'lucide-react';
import type { SocialPost, PetForSocial } from './socialTypes';
import { resolveTaggedPets } from './socialTypes';
import { SocialPostImage } from './SocialPostImage';
import { toast } from 'sonner';
import { usePetPhoto } from '../../hooks/usePetPhoto';
import { useSocialFeed } from '../../context/SocialFeedContext';
import { CreatePost } from './CreatePost';
import * as socialApi from '@/api/social';
import petsoPaw from '../../assets/78751f73824316be9d04898afd4169ed5ee341b1.png';

const ALL_PETS_ID = '__all__';

export type ProfilePrivacy =
  | 'public'
  | 'private'
  | 'friends'
  | 'friends_plus'
  | 'followers';

function getProfilePrivacyOptions(
  t: (key: string) => string
): { value: ProfilePrivacy; label: string }[] {
  return [
    { value: 'public', label: t('social.public') },
    { value: 'private', label: t('social.private') },
    { value: 'friends', label: t('social.friends') },
    { value: 'friends_plus', label: t('social.friendsPlus') },
    { value: 'followers', label: t('social.followers') },
  ];
}

/** Pet with optional profile-only fields */
type PetForProfile = PetForSocial & {
  breed?: string;
  followers?: number;
  following?: number;
  posts?: number;
  bio?: string;
};

/** Renders pet avatar using same source as Overview (usePetPhoto). */
function PetProfileAvatar({
  petId,
  photoKey,
  imageUrl,
  name,
  species,
  className = 'w-8 h-8',
}: {
  petId: string;
  photoKey?: string;
  imageUrl?: string;
  name: string;
  species?: string;
  className?: string;
}) {
  const { signedImageUrl } = usePetPhoto({ petId, photoKey, imageUrl });
  const emoji = species === 'dog' ? '🐕' : species === 'cat' ? '🐈' : '🐾';
  return (
    <div
      className={`rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 ${className}`}
      style={{
        background: 'linear-gradient(135deg, #F0EFF5 0%, #936FEF 100%)',
      }}
    >
      {signedImageUrl ? (
        <img
          src={signedImageUrl}
          alt={name}
          className="w-full h-full object-cover"
        />
      ) : (
        <span className="text-xl">{emoji}</span>
      )}
    </div>
  );
}

interface PetSocialProfileProps {
  userType: 'owner' | 'professional';
  userId: string;
  userDisplayName?: string;
  userAvatarUrl?: string;
  pets: PetForProfile[];
  /** Posts (owner-based) for Social Activity: show posts where this pet is tagged */
  posts?: SocialPost[];
  /** When navigating from feed "View pet" → open profile with this pet selected */
  initialSelectedPetId?: string | null;
  /** Called once after applying initialSelectedPetId so we don't re-apply on re-mount */
  onInitialPetConsumed?: () => void;
  /** When provided (owner), profile can edit/delete posts and update the list */
  onPostsChange?: (posts: SocialPost[]) => void;
  userFollowers?: number;
  userFollowing?: number;
  onViewUserProfile?: (userId: string, name: string, avatar?: string | null, type?: 'USER' | 'PET') => void;
}

/** Pets as PetForSocial for resolveTaggedPets */
const petsToForSocial = (pets: PetForProfile[]): PetForSocial[] =>
  pets.map((p) => ({
    id: p.id,
    name: p.name ?? 'Unknown',
    species: p.species,
    avatar:
      p.avatar ||
      (p.species === 'dog' ? '🐕' : p.species === 'cat' ? '🐈' : '🐾'),
    imageUrl: p.imageUrl,
    photoKey: p.photoKey,
  }));

export function PetSocialProfile({
  userType,
  userId,
  userDisplayName,
  userAvatarUrl,
  pets,
  posts = [],
  initialSelectedPetId,
  onInitialPetConsumed,
  onPostsChange,
  userFollowers = 0,
  userFollowing = 0,
  onViewUserProfile,
}: PetSocialProfileProps) {
  const { t } = useTranslation();
  const profilePrivacyOptions = useMemo(() => getProfilePrivacyOptions(t), [t]);
  const socialFeed = useSocialFeed();
  const [activeTab, setActiveTab] = useState<
    'posts' | 'tagged' | 'saved' | 'activity'
  >('posts');
  /** Post menu open (•••) for Edit/Delete on profile grid */
  const [profilePostMenuId, setProfilePostMenuId] = useState<string | null>(
    null
  );
  /** Post being edited (owner only); when set, Edit Post modal is open */
  const [editingPost, setEditingPost] = useState<SocialPost | null>(null);

  /** Followers/Following list modal */
  const [followListType, setFollowListType] = useState<'followers' | 'following' | null>(null);
  const [followList, setFollowList] = useState<socialApi.FollowerOrFollowingItem[]>([]);
  const [followListLoading, setFollowListLoading] = useState(false);

  const openFollowList = useCallback(async (type: 'followers' | 'following') => {
    setFollowListType(type);
    setFollowList([]);
    setFollowListLoading(true);
    try {
      if (type === 'followers') {
        const { followers } = await socialApi.getFollowers({ limit: 200 });
        setFollowList(followers);
      } else {
        const { following } = await socialApi.getFollowing({ limit: 200 });
        setFollowList(following);
      }
    } catch {
      // silently ignore
    } finally {
      setFollowListLoading(false);
    }
  }, []);

  useEffect(() => {
    setProfilePostMenuId(null);
  }, [activeTab]);
  /** Selected pet id, or ALL_PETS_ID for "All", or single pet when only one */
  const [selectedPet, setSelectedPet] = useState<string | null>(
    pets.length === 1
      ? (pets[0]?.id ?? null)
      : initialSelectedPetId && pets.some((p) => p.id === initialSelectedPetId)
        ? initialSelectedPetId
        : ALL_PETS_ID
  );
  const [petDropdownOpen, setPetDropdownOpen] = useState(false);
  const [privacyDropdownOpen, setPrivacyDropdownOpen] = useState(false);
  const petDropdownRef = useRef<HTMLDivElement>(null);
  const privacyDropdownRef = useRef<HTMLDivElement>(null);

  /** Per-pet profile privacy (view/search). When "All" is selected and user updates, we apply to all. */
  const [privacyByPetId, setPrivacyByPetId] = useState<
    Record<string, ProfilePrivacy>
  >(() => {
    const ids = pets.map((p) => p.id).filter(Boolean) as string[];
    const initial: Record<string, ProfilePrivacy> = {};
    ids.forEach((id) => {
      initial[id] = 'public';
    });
    return initial;
  });

  useEffect(() => {
    if (
      initialSelectedPetId != null &&
      pets.some((p) => p.id === initialSelectedPetId)
    ) {
      setSelectedPet(initialSelectedPetId);
      onInitialPetConsumed?.();
    }
  }, [initialSelectedPetId, pets, onInitialPetConsumed]);

  // Ensure new pets get a default privacy
  useEffect(() => {
    setPrivacyByPetId((prev) => {
      const next = { ...prev };
      pets.forEach((p) => {
        if (p.id && next[p.id] === undefined) next[p.id] = 'public';
      });
      return next;
    });
  }, [pets]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (petDropdownRef.current && !petDropdownRef.current.contains(target)) {
        setPetDropdownOpen(false);
      }
      if (
        privacyDropdownRef.current &&
        !privacyDropdownRef.current.contains(target)
      ) {
        setPrivacyDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Post counts per pet (for stats): posts where this user is owner and pet is tagged
  const postCountByPetId = useMemo(() => {
    const counts: Record<string, number> = {};
    pets.forEach((pet) => {
      if (pet.id) counts[pet.id] = 0;
    });
    posts.forEach((p) => {
      if (p.ownerId !== userId) return;
      (p.taggedPetIds ?? []).forEach((id) => {
        if (counts[id] !== undefined) counts[id]++;
      });
    });
    return counts;
  }, [pets, posts, userId]);

  const mockPets =
    pets.length > 0
      ? pets.map((pet) => ({
          id: pet.id || '1',
          name: pet.name || 'Unknown',
          species: pet.species || 'Pet',
          breed: pet.breed || 'Mixed',
          avatar:
            pet.avatar ||
            (pet.species === 'dog'
              ? '🐕'
              : pet.species === 'cat'
                ? '🐈'
                : '🐾'),
          followers: pet.followers ?? 0,
          following: pet.following ?? 0,
          posts: postCountByPetId[pet.id ?? ''] ?? 0,
        }))
      : [
          {
            id: '1',
            name: t('social.yourPet'),
            species: 'Pet',
            breed: '—',
            avatar: '🐾',
            followers: 0,
            following: 0,
            posts: 0,
          },
        ];

  const isAllPets = selectedPet === ALL_PETS_ID;
  const totalMyPosts = useMemo(
    () => posts.filter((p) => p.ownerId === userId).length,
    [posts, userId]
  );
  const currentPet = isAllPets
    ? {
        id: ALL_PETS_ID,
        name: t('social.allPets'),
        species: '—',
        breed: '—',
        avatar: '🐾',
        followers: mockPets.reduce((s, p) => s + (p.followers ?? 0), 0),
        following: mockPets.reduce((s, p) => s + (p.following ?? 0), 0),
        posts: totalMyPosts,
      }
    : mockPets.find((p) => p.id === selectedPet) || mockPets[0];

  // Use same photo source as Overview (signed URL from photoKey/imageUrl). For "All pets", show Petso paw logo.
  const currentPetSource = isAllPets
    ? null
    : pets.find((p) => p.id === selectedPet);
  const { signedImageUrl } = usePetPhoto({
    petId: currentPetSource?.id ?? '',
    photoKey: currentPetSource?.photoKey,
    imageUrl: currentPetSource?.imageUrl,
  });

  // Posts tab: only photo/video posts by this user (no text-only). When a single pet is selected, only posts where that pet is tagged.
  const petPosts = (
    isAllPets
      ? posts.filter((p) => p.ownerId === userId)
      : selectedPet
        ? posts.filter(
            (p) =>
              p.ownerId === userId &&
              (p.taggedPetIds ?? []).includes(selectedPet)
          )
        : []
  ).filter((p) => (p.images?.length ?? 0) > 0);

  // Tagged tab: posts where the selected pet (or any of user's pets when "All") is tagged – by owner or anyone
  const taggedPosts = isAllPets
    ? posts.filter((p) =>
        (p.taggedPetIds ?? []).some((id) => mockPets.some((mp) => mp.id === id))
      )
    : selectedPet
      ? posts.filter((p) => (p.taggedPetIds ?? []).includes(selectedPet))
      : [];

  // Social Activity: all of the user's posts (photo, video, text) – same logic as before but include text-only
  const socialActivityPosts = isAllPets
    ? posts.filter((p) => p.ownerId === userId)
    : selectedPet
      ? posts.filter(
          (p) =>
            p.ownerId === userId && (p.taggedPetIds ?? []).includes(selectedPet)
        )
      : [];

  // Effective privacy for current selection: when "All", use first pet's (after "apply to all" they match)
  const effectivePrivacy: ProfilePrivacy = isAllPets
    ? (mockPets[0]?.id && privacyByPetId[mockPets[0].id]) || 'public'
    : (selectedPet && privacyByPetId[selectedPet]) || 'public';

  const setPrivacyForSelection = (value: ProfilePrivacy) => {
    if (isAllPets) {
      setPrivacyByPetId((prev) => {
        const next = { ...prev };
        mockPets.forEach((p) => {
          if (p.id) next[p.id] = value;
        });
        return next;
      });
      toast.success(
        `Profile privacy set to ${profilePrivacyOptions.find((o) => o.value === value)?.label ?? value} for all pets.`
      );
    } else if (selectedPet) {
      setPrivacyByPetId((prev) => ({ ...prev, [selectedPet]: value }));
      toast.success(
        `Profile privacy set to ${profilePrivacyOptions.find((o) => o.value === value)?.label ?? value}.`
      );
    }
    setPrivacyDropdownOpen(false);
  };

  const canEditDelete =
    userType === 'owner' && (onPostsChange != null || socialFeed != null);

  const canEditPost = useCallback(
    (post: SocialPost) => {
      if (post.ownerId !== userId) return false;
      const created = post.createdAt;
      if (created == null) return true;
      const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
      return Date.now() - created < TWENTY_FOUR_HOURS_MS;
    },
    [userId]
  );

  const handleLikePost = useCallback(
    (postId: string) => {
      const post = posts.find((p) => p.id === postId);
      if (!post) return;
      if (post.isLiked) {
        socialFeed.unlikePost(postId);
      } else {
        socialFeed.likePost(postId);
      }
    },
    [socialFeed, posts]
  );

  const handleDeletePost = useCallback(
    (postId: string) => {
      socialFeed.deletePost(postId);
      toast.success(t('social.postDeleted'));
    },
    [socialFeed]
  );

  const handleOpenEditPost = useCallback((post: SocialPost) => {
    setEditingPost(post);
    setEditNewImageUrls([]);
  }, []);

  const handleEditPostUpdated = useCallback(
    (
      id: string,
      updates: {
        caption: string;
        taggedPetIds: string[];
        images: string[];
        location?: string;
        visibility: 'public' | 'followers' | 'friends' | 'friends-of-friends' | 'private';
      }
    ) => {
      const petsForSocial = petsToForSocial(pets);
      const taggedPets = resolveTaggedPets(updates.taggedPetIds, petsForSocial);
      socialFeed.updatePost(
        id,
        {
          caption: updates.caption,
          taggedPetIds: updates.taggedPetIds,
          taggedPetSummaries: taggedPets,
          images: updates.images,
          location: updates.location,
          visibility: updates.visibility,
        },
        taggedPets
      );
      toast.success(t('social.postUpdated'));
      setEditingPost(null);
    },
    [socialFeed, pets, t]
  );

  return (
    <div className="py-4 sm:py-6 px-3 sm:px-4">
      <div className="max-w-4xl mx-auto min-w-0">
        {/* Profile Header – no horizontal pet row; pet chosen via dropdown next to name */}
        <div className="bg-white dark:bg-[#16151D] rounded-2xl p-4 sm:p-6 md:p-8 mb-4 sm:mb-6 border border-[#E2DFEA] dark:border-white/8 min-w-0 overflow-hidden">
          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 md:gap-8 mb-4 sm:mb-6 min-w-0">
            {/* Profile Picture – owner avatar */}
            <div
              className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full flex items-center justify-center text-6xl flex-shrink-0 overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #F0EFF5 0%, #936FEF 100%)',
              }}
            >
              {userAvatarUrl ? (
                <img
                  src={userAvatarUrl}
                  alt={userDisplayName || 'Profile'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-4xl">👤</span>
              )}
            </div>

            {/* Profile Info */}
            <div className="flex-1 min-w-0 w-full sm:w-auto">
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-3 sm:mb-4 min-w-0">
                {/* Owner name */}
                <h2
                  className="text-[#2D2F7E] dark:text-white truncate"
                  style={{
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    letterSpacing: '-0.02em',
                  }}
                >
                  {userDisplayName || t('social.profile.myProfile')}
                </h2>

                {/* Pet filter dropdown */}
                <div className="relative min-w-0" ref={petDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setPetDropdownOpen((o) => !o)}
                    className="flex items-center gap-2 rounded-xl border border-[#E2DFEA] dark:border-white/20 bg-white dark:bg-[#16151D] px-3 sm:px-4 py-2 sm:py-2.5 text-left min-w-0 max-w-full hover:bg-[#F0EFF5] dark:hover:bg-white/5 transition-colors touch-manipulation"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                    aria-expanded={petDropdownOpen}
                    aria-haspopup="listbox"
                    aria-label="Select pet"
                  >
                    <span className="text-[#2D2F7E] dark:text-white truncate text-sm">
                      {t('social.posts')}: {currentPet.name}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 flex-shrink-0 text-[#464452] dark:text-white/70 transition-transform ${
                        petDropdownOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {petDropdownOpen && (
                    <ul
                      className="absolute top-full left-0 mt-1 z-50 min-w-[12rem] py-1 rounded-xl border border-[#E2DFEA] dark:border-white/20 bg-white dark:bg-[#16151D] shadow-lg max-h-60 overflow-auto"
                      role="listbox"
                    >
                      {mockPets.length > 1 && (
                        <li role="option" aria-selected={isAllPets}>
                          <button
                            type="button"
                            className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-[#F0EFF5] dark:hover:bg-white/10 text-[#2D2F7E] dark:text-white"
                            style={{ fontFamily: 'Inter, sans-serif' }}
                            onClick={() => {
                              setSelectedPet(ALL_PETS_ID);
                              setPetDropdownOpen(false);
                            }}
                          >
                            <span className="text-lg">🐾</span>
                            All
                          </button>
                        </li>
                      )}
                      {mockPets.map((pet) => {
                        const source = pets.find((p) => p.id === pet.id);
                        return (
                          <li
                            key={pet.id}
                            role="option"
                            aria-selected={selectedPet === pet.id}
                          >
                            <button
                              type="button"
                              className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-[#F0EFF5] dark:hover:bg-white/10 text-[#2D2F7E] dark:text-white"
                              style={{ fontFamily: 'Inter, sans-serif' }}
                              onClick={() => {
                                setSelectedPet(pet.id);
                                setPetDropdownOpen(false);
                              }}
                            >
                              <PetProfileAvatar
                                petId={source?.id ?? pet.id}
                                photoKey={source?.photoKey}
                                imageUrl={source?.imageUrl}
                                name={pet.name}
                                species={pet.species}
                                className="w-8 h-8"
                              />
                              {pet.name}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                {userType === 'owner' && (
                  <div className="relative min-w-0" ref={privacyDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setPrivacyDropdownOpen((o) => !o)}
                      className="flex items-center gap-2 rounded-xl border border-[#E2DFEA] dark:border-white/20 bg-white dark:bg-[#16151D] px-3 sm:px-4 py-2 sm:py-2.5 text-left min-w-0 max-w-full hover:bg-[#F0EFF5] dark:hover:bg-white/5 transition-colors touch-manipulation"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                      aria-expanded={privacyDropdownOpen}
                      aria-haspopup="listbox"
                      aria-label="Profile privacy"
                    >
                      {effectivePrivacy === 'private' ? (
                        <Lock className="w-5 h-5 text-[#464452] dark:text-white/70 flex-shrink-0" />
                      ) : (
                        <Unlock className="w-5 h-5 text-[#464452] dark:text-white/70 flex-shrink-0" />
                      )}
                      <span className="text-[#2D2F7E] dark:text-white truncate">
                        <span className="hidden sm:inline">
                          {t('social.profilePrivacy')}:{' '}
                        </span>
                        {profilePrivacyOptions.find(
                          (o) => o.value === effectivePrivacy
                        )?.label ?? effectivePrivacy}
                      </span>
                      <ChevronDown
                        className={`w-5 h-5 flex-shrink-0 text-[#464452] dark:text-white/70 transition-transform ${
                          privacyDropdownOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {privacyDropdownOpen && (
                      <ul
                        className="absolute top-full left-0 mt-1 z-50 min-w-[10rem] py-1 rounded-xl border border-[#E2DFEA] dark:border-white/20 bg-white dark:bg-[#16151D] shadow-lg max-h-60 overflow-auto"
                        role="listbox"
                      >
                        {profilePrivacyOptions.map((opt) => (
                          <li
                            key={opt.value}
                            role="option"
                            aria-selected={effectivePrivacy === opt.value}
                          >
                            <button
                              type="button"
                              className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-[#F0EFF5] dark:hover:bg-white/10 text-[#2D2F7E] dark:text-white"
                              style={{ fontFamily: 'Inter, sans-serif' }}
                              onClick={() => setPrivacyForSelection(opt.value)}
                            >
                              {opt.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                {userType === 'professional' && (
                  <button
                    className="px-6 py-2 rounded-xl text-white hover:opacity-90 transition-opacity"
                    style={{
                      background:
                        'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                      fontFamily: 'Sora, sans-serif',
                      fontWeight: 700,
                    }}
                  >
                    {t('social.profile.follow')}
                  </button>
                )}
              </div>

              {/* Stats - wrap on mobile so "Following" is never truncated */}
              <div
                className="flex flex-wrap gap-4 sm:gap-6 md:gap-8 mb-4 min-w-0"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                <div className="whitespace-nowrap">
                  <span className="font-semibold text-[#2D2F7E] dark:text-white">
                    {currentPet.posts}
                  </span>
                  <span className="ml-1 text-[#464452] dark:text-white/70">
                    {t('social.posts')}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => openFollowList('followers')}
                  className="text-[#464452] dark:text-white/70 hover:text-[#936FEF] transition-colors whitespace-nowrap touch-manipulation"
                >
                  <span className="font-semibold text-[#2D2F7E] dark:text-white">
                    {userFollowers.toLocaleString()}
                  </span>
                  <span className="ml-1">{t('social.followers')}</span>
                </button>
                <button
                  type="button"
                  onClick={() => openFollowList('following')}
                  className="text-[#464452] dark:text-white/70 hover:text-[#936FEF] transition-colors whitespace-nowrap touch-manipulation"
                >
                  <span className="font-semibold text-[#2D2F7E] dark:text-white">
                    {userFollowing.toLocaleString()}
                  </span>
                  <span className="ml-1">{t('social.following')}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Privacy Notice */}
          <div
            className="rounded-xl p-3 sm:p-4 text-sm bg-gradient-to-r from-purple-50 to-purple-100/10 dark:from-purple-900/20 dark:to-purple-800/10 border border-[#E2DFEA] dark:border-white/10 text-[#2D2F7E] dark:text-white break-words min-w-0"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {effectivePrivacy === 'private' ? (
              <>
                <strong>🔒 {t('social.privacyProtectedLabel')}:</strong>{' '}
                {t('social.privacyProtectedMessage')}
              </>
            ) : (
              <>
                <strong>🔓 {t('social.profile.publicProfile', 'Public profile')}:</strong>{' '}
                {t('social.profile.publicProfileMessage', 'Your posts are visible based on your privacy setting.')}
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-[#16151D] rounded-t-2xl border border-[#E2DFEA] dark:border-white/8 min-w-0 overflow-hidden">
          <div className="flex border-b border-[#E2DFEA] dark:border-white/8 min-w-0">
            <button
              onClick={() => setActiveTab('posts')}
              className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 py-3 sm:py-4 transition-colors min-w-0 px-1 touch-manipulation ${
                activeTab === 'posts'
                  ? 'border-b-2 border-[#936FEF] text-[#936FEF]'
                  : 'text-[#464452] dark:text-white/70'
              }`}
            >
              <Grid className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
              <span
                className="truncate"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('social.posts')}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('tagged')}
              className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 py-3 sm:py-4 transition-colors min-w-0 px-1 touch-manipulation ${
                activeTab === 'tagged'
                  ? 'border-b-2 border-[#936FEF] text-[#936FEF]'
                  : 'text-[#464452] dark:text-white/70'
              }`}
            >
              <Tag className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
              <span
                className="truncate"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('social.tagged')}
              </span>
            </button>
            {userType === 'owner' && (
              <button
                onClick={() => setActiveTab('saved')}
                className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 py-3 sm:py-4 transition-colors min-w-0 px-1 touch-manipulation ${
                  activeTab === 'saved'
                    ? 'border-b-2 border-[#936FEF] text-[#936FEF]'
                    : 'text-[#464452] dark:text-white/70'
                }`}
              >
                <Heart className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                <span
                  className="truncate"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('social.saved')}
                </span>
              </button>
            )}
            <button
              onClick={() => setActiveTab('activity')}
              className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 py-3 sm:py-4 transition-colors min-w-0 px-1 touch-manipulation ${
                activeTab === 'activity'
                  ? 'border-b-2 border-[#936FEF] text-[#936FEF]'
                  : 'text-[#464452] dark:text-white/70'
              }`}
            >
              <Users className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
              <span
                className="truncate"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('social.socialActivity')}
              </span>
            </button>
          </div>
        </div>

        {/* Posts Grid */}
        <div className="bg-white dark:bg-[#16151D] rounded-b-2xl p-4 sm:p-6 border-x border-b border-[#E2DFEA] dark:border-white/8 min-w-0 overflow-hidden">
          {activeTab === 'posts' &&
            (petPosts.length === 0 ? (
              <div className="text-center py-12">
                <Grid className="w-16 h-16 mx-auto mb-4 text-[#464452] dark:text-white/20 opacity-20" />
                <p
                  className="text-[#464452] dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('social.shareFirstPost')}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-1">
                {petPosts.map((post) => (
                  <div
                    key={post.id}
                    className="aspect-square relative group overflow-hidden rounded-lg"
                  >
                    {/* More menu button - always visible for own posts */}
                    {canEditDelete && (
                      <div className="absolute top-1 right-1 z-10">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setProfilePostMenuId(
                              profilePostMenuId === post.id ? null : post.id
                            );
                          }}
                          className="p-1.5 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
                          aria-label="Post options"
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                        {profilePostMenuId === post.id && (
                          <>
                            <div
                              className="fixed inset-0 z-40"
                              aria-hidden
                              onClick={() => setProfilePostMenuId(null)}
                            />
                            <div
                              className="absolute right-0 top-full mt-1 py-1 min-w-[140px] rounded-lg bg-white dark:bg-[#16151D] border border-[#E2DFEA] dark:border-white/10 shadow-lg z-50"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {canEditPost(post) && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleOpenEditPost(post);
                                    setProfilePostMenuId(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm hover:bg-[#F0EFF5] dark:hover:bg-white/10"
                                  style={{ fontFamily: 'Inter, sans-serif' }}
                                >
                                  <Pencil className="w-4 h-4 text-[#936FEF]" />
                                  Edit
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  handleDeletePost(post.id);
                                  setProfilePostMenuId(null);
                                }}
                                className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                style={{ fontFamily: 'Inter, sans-serif' }}
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    {post.images?.[0] ? (
                      <SocialPostImage
                        src={post.images[0]}
                        alt="Post"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center p-2 overflow-hidden bg-[#F0EFF5] dark:bg-white/10">
                        <p className="text-[#464452] dark:text-white/70 text-xs line-clamp-4 text-center break-words">
                          {post.caption || t('social.textPost')}
                        </p>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                      {post.editedAt != null && (
                        <div className="text-white/90 text-xs">Edited</div>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLikePost(post.id);
                        }}
                        className="flex items-center gap-2 text-white hover:opacity-90 transition-opacity"
                        title={post.isLiked ? 'Unlike' : 'Like'}
                      >
                        <Heart
                          className={`w-5 h-5 ${
                            post.isLiked ? 'fill-[#936FEF]' : 'fill-white'
                          }`}
                        />
                        <span>{post.likes}</span>
                      </button>
                      {canEditDelete && (
                        <div className="flex items-center gap-2">
                          {canEditPost(post) && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEditPost(post);
                              }}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm"
                              style={{ fontFamily: 'Inter, sans-serif' }}
                            >
                              <Pencil className="w-4 h-4" />
                              Edit
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePost(post.id);
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/80 hover:bg-red-500 text-white text-sm"
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}

          {activeTab === 'tagged' && (
            <div className="space-y-4">
              <p
                className="text-sm text-[#464452] dark:text-white/70 mb-4"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {isAllPets
                  ? t('social.taggedDescription')
                  : t('social.taggedDescriptionPet', { name: currentPet.name })}
              </p>
              {taggedPosts.length === 0 ? (
                <div className="text-center py-12">
                  <Tag className="w-16 h-16 mx-auto mb-4 text-[#464452] dark:text-white/20 opacity-20" />
                  <p
                    className="text-[#464452] dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {isAllPets
                      ? t('social.noTaggedYet')
                      : t('social.noTaggedYetPet', { name: currentPet.name })}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-1">
                  {taggedPosts.map((post) => (
                    <div
                      key={post.id}
                      className="aspect-square rounded-lg overflow-hidden bg-[#F0EFF5] dark:bg-white/5 flex flex-col"
                    >
                      {post.images?.[0] ? (
                        <SocialPostImage
                          src={post.images[0]}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center p-2 overflow-hidden">
                          <p className="text-[#464452] dark:text-white/70 text-xs line-clamp-4 text-center break-words">
                            {post.caption || t('social.textPost')}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'saved' && userType === 'owner' && (
            <div className="text-center py-12">
              <Heart className="w-16 h-16 mx-auto mb-4 text-[#464452] dark:text-white/20 opacity-20" />
              <p
                className="text-[#464452] dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Your saved posts
              </p>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-4">
              <p
                className="text-sm text-[#464452] dark:text-white/70 mb-4"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {isAllPets
                  ? t('social.postsDescription')
                  : `Posts by you for ${currentPet.name}`}
              </p>
              {socialActivityPosts.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 mx-auto mb-4 text-[#464452] dark:text-white/20 opacity-20" />
                  <p
                    className="text-[#464452] dark:text-white/70"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    No posts yet. Share from the Create tab!
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-1">
                  {socialActivityPosts.map((post) => (
                    <div
                      key={post.id}
                      className="aspect-square rounded-lg overflow-hidden bg-[#F0EFF5] dark:bg-white/5 flex flex-col relative"
                    >
                      {canEditDelete && (
                        <div className="absolute top-1 right-1 z-10">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setProfilePostMenuId(
                                profilePostMenuId === post.id ? null : post.id
                              );
                            }}
                            className="p-1.5 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
                            aria-label="Post options"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                          {profilePostMenuId === post.id && (
                            <>
                              <div
                                className="fixed inset-0 z-40"
                                aria-hidden
                                onClick={() => setProfilePostMenuId(null)}
                              />
                              <div
                                className="absolute right-0 top-full mt-1 py-1 min-w-[140px] rounded-lg bg-white dark:bg-[#16151D] border border-[#E2DFEA] dark:border-white/10 shadow-lg z-50"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {canEditPost(post) && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      handleOpenEditPost(post);
                                      setProfilePostMenuId(null);
                                    }}
                                    className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm hover:bg-[#F0EFF5] dark:hover:bg-white/10"
                                    style={{ fontFamily: 'Inter, sans-serif' }}
                                  >
                                    <Pencil className="w-4 h-4 text-[#936FEF]" />
                                    Edit
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleDeletePost(post.id);
                                    setProfilePostMenuId(null);
                                  }}
                                  className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                  style={{ fontFamily: 'Inter, sans-serif' }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Delete
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      {post.images?.[0] ? (
                        <SocialPostImage
                          src={post.images[0]}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div
                          className="w-full h-full flex items-center justify-center p-2 overflow-hidden"
                          style={{ fontFamily: 'Inter, sans-serif' }}
                        >
                          <p className="text-[#464452] dark:text-white/70 text-xs line-clamp-4 text-center break-words">
                            {post.caption || t('social.textPost')}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Followers / Following List Modal */}
      {followListType && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setFollowListType(null)}
        >
          <div
            className="bg-white dark:bg-[#16151D] rounded-2xl border border-[#E2DFEA] dark:border-white/8 max-w-md w-full max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-[#E2DFEA] dark:border-white/8">
              <h2
                className="text-lg text-[#2D2F7E] dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {followListType === 'followers' ? t('social.followers') : t('social.following')}
              </h2>
              <button
                type="button"
                onClick={() => setFollowListType(null)}
                className="p-2 rounded-lg hover:bg-[#F0EFF5] dark:hover:bg-white/5 text-[#464452] dark:text-white/70"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-2">
              {followListLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#936FEF]" />
                </div>
              ) : followList.length === 0 ? (
                <p
                  className="text-center text-[#464452] dark:text-white/70 py-8 text-sm"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {followListType === 'followers'
                    ? t('social.profile.noFollowers', 'No followers yet')
                    : t('social.profile.noFollowing', 'Not following anyone yet')}
                </p>
              ) : (
                followList.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    onClick={() => {
                      if (onViewUserProfile && item.id) {
                        setFollowListType(null);
                        onViewUserProfile(item.id, item.name ?? 'Unknown', item.avatar, item.type);
                      }
                    }}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#F0EFF5] dark:hover:bg-white/5 w-full text-left cursor-pointer transition-colors"
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0"
                      style={{
                        background: 'linear-gradient(135deg, #F0EFF5 0%, #936FEF 100%)',
                      }}
                    >
                      {item.avatar ? (
                        <img
                          src={item.avatar}
                          alt={item.name ?? ''}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-lg">{item.type === 'PET' ? '🐾' : '👤'}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-sm font-medium text-[#2D2F7E] dark:text-white truncate"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {item.name ?? t('social.unknownUser', 'Unknown')}
                      </p>
                      <p
                        className="text-xs text-[#464452] dark:text-white/50"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {item.type === 'PET' ? t('social.pet', 'Pet') : t('social.user', 'User')}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Post Modal – reuses full CreatePost in edit mode */}
      {editingPost && (
        <CreatePost
          userType={userType}
          userId={userId}
          ownerName={userDisplayName}
          ownerAvatar={userAvatarUrl}
          pets={petsToForSocial(pets)}
          onClose={() => setEditingPost(null)}
          onPostCreated={() => {}}
          editingPost={{
            id: editingPost.id,
            images: editingPost.images,
            caption: editingPost.caption,
            taggedPetIds: editingPost.taggedPetIds ?? [],
            location: editingPost.location,
            visibility: (editingPost.visibility as 'public' | 'followers' | 'friends' | 'friends-of-friends' | 'private') ?? 'public',
          }}
          onPostUpdated={handleEditPostUpdated}
        />
      )}
    </div>
  );
}
